/**
 * 图片处理工具类
 * 主要用于处理阿里云OSS图片的裁剪、压缩和格式转换
 * 增强版本：支持WebP检测、缓存、预加载等功能
 */

// 缓存配置
interface CacheConfig {
  maxSize: number; // 最大缓存大小（MB）
  maxAge: number; // 最大缓存时间（毫秒）
}

// URL缓存项
interface CachedUrl {
  url: string;
  timestamp: number;
  size: number; // 估算大小（字节）
}

export class ImageProcessor {
  private static urlCache = new Map<string, CachedUrl>();
  private static webpSupported: boolean | null = null;
  private static cacheConfig: CacheConfig = {
    maxSize: 50, // 50MB
    maxAge: 30 * 60 * 1000, // 30分钟
  };

  static {
    // 静态初始化
    this.detectWebPSupport();
    this.startCacheCleanup();
  }
  /**
   * 检测浏览器是否支持WebP格式
   */
  private static async detectWebPSupport(): Promise<boolean> {
    if (this.webpSupported !== null) {
      return this.webpSupported;
    }

    return new Promise(resolve => {
      const webP = new Image();
      webP.onload = webP.onerror = () => {
        this.webpSupported = webP.height === 2;
        resolve(this.webpSupported);
      };
      webP.src =
        'data:image/webp;base64,UklGRjoAAABXRUJQVlA4IC4AAACyAgCdASoCAAIALmk0mk0iIiIiIgBoSygABc6WWgAA/veff/0PP8bA//LwYAAA';
    });
  }

  /**
   * 获取WebP支持状态
   */
  public static async isWebPSupported(): Promise<boolean> {
    if (this.webpSupported !== null) {
      return this.webpSupported;
    }
    return this.detectWebPSupport();
  }

  /**
   * 生成缓存键
   */
  private static generateCacheKey(url: string, options: any): string {
    const optionsStr = JSON.stringify(options);
    return `${url}|${optionsStr}`;
  }

  /**
   * 清理过期缓存
   */
  private static startCacheCleanup(): void {
    setInterval(
      () => {
        const now = Date.now();
        const expiredKeys: string[] = [];

        for (const [key, cached] of this.urlCache.entries()) {
          if (now - cached.timestamp > this.cacheConfig.maxAge) {
            expiredKeys.push(key);
          }
        }

        expiredKeys.forEach(key => this.urlCache.delete(key));

        // 如果缓存大小超限，删除最旧的条目
        this.limitCacheSize();
      },
      5 * 60 * 1000
    ); // 每5分钟清理一次
  }

  /**
   * 限制缓存大小
   */
  private static limitCacheSize(): void {
    const maxSizeBytes = this.cacheConfig.maxSize * 1024 * 1024;
    let totalSize = 0;

    // 计算总大小
    for (const cached of this.urlCache.values()) {
      totalSize += cached.size;
    }

    if (totalSize <= maxSizeBytes) {
      return;
    }

    // 按时间戳排序，删除最旧的条目
    const entries = Array.from(this.urlCache.entries()).sort(
      ([, a], [, b]) => a.timestamp - b.timestamp
    );

    for (const [key, cached] of entries) {
      this.urlCache.delete(key);
      totalSize -= cached.size;

      if (totalSize <= maxSizeBytes) {
        break;
      }
    }
  }

  /**
   * 处理阿里云OSS图片URL，添加裁剪和优化参数
   * @param url 原始图片URL
   * @param width 目标宽度
   * @param height 目标高度
   * @param mode 裁剪模式：fill(智能裁剪), fit(等比缩放), pad(等比缩放填充)
   * @param quality 图片质量 (1-100)
   * @param format 输出格式
   * @returns 处理后的图片URL
   */
  static async processOSSImage(
    url: string,
    width: number,
    height: number,
    options: {
      mode?: 'fill' | 'fit' | 'pad';
      quality?: number;
      format?: 'webp' | 'jpg' | 'png' | 'auto';
      autoOrient?: boolean;
      useCache?: boolean;
    } = {}
  ): Promise<string> {
    if (!url) return url;

    const {
      mode = 'fill',
      quality = 50,
      format = 'auto',
      autoOrient = true,
      useCache = true,
    } = options;

    // 检查缓存
    if (useCache) {
      const cacheKey = this.generateCacheKey(url, { width, height, ...options });
      const cached = this.urlCache.get(cacheKey);

      if (cached && Date.now() - cached.timestamp < this.cacheConfig.maxAge) {
        return cached.url;
      }
    }

    // 检查是否为阿里云OSS图片
    if (!this.isOSSImage(url)) {
      return url;
    }

    // 自动检测WebP支持
    let finalFormat = format;
    if (format === 'auto') {
      finalFormat = (await this.isWebPSupported()) ? 'webp' : 'jpg';
    }

    const params = [
      `resize,m_${mode},w_${width},h_${height}`,
      ...(autoOrient ? ['auto-orient,1'] : []),
      `quality,q_${quality}`,
      ...(finalFormat !== 'auto' ? [`format,${finalFormat}`] : []),
    ].join('/');

    const processedUrl = `${url}?x-oss-process=image/${params}`;

    // 缓存结果
    if (useCache) {
      const cacheKey = this.generateCacheKey(url, { width, height, ...options });
      const estimatedSize = this.estimateImageSize(width, height, quality, finalFormat);
      this.urlCache.set(cacheKey, {
        url: processedUrl,
        timestamp: Date.now(),
        size: estimatedSize,
      });
    }

    return processedUrl;
  }

  /**
   * 估算图片大小（用于缓存管理）
   */
  private static estimateImageSize(
    width: number,
    height: number,
    quality: number,
    format: string
  ): number {
    const baseSize = width * height;
    const qualityFactor = quality / 100;

    switch (format) {
      case 'webp':
        return Math.round(baseSize * qualityFactor * 0.3); // WebP压缩率更高
      case 'jpg':
      case 'jpeg':
        return Math.round(baseSize * qualityFactor * 0.5);
      case 'png':
        return Math.round(baseSize * 1.2); // PNG无损，文件较大
      default:
        return Math.round(baseSize * qualityFactor * 0.5);
    }
  }

  /**
   * 批量处理图片URL
   * @param urls 图片URL数组
   * @param width 目标宽度
   * @param height 目标高度
   * @param options 处理选项
   * @returns 处理后的图片URL数组
   */
  static async processBatchImages(
    urls: string[],
    width: number,
    height: number,
    options?: {
      mode?: 'fill' | 'fit' | 'pad';
      quality?: number;
      format?: 'webp' | 'jpg' | 'png' | 'auto';
      autoOrient?: boolean;
    }
  ): Promise<string[]> {
    return Promise.all(urls.map(url => this.processOSSImage(url, width, height, options)));
  }

  /**
   * 为文件夹封面处理图片
   * @param covers 封面图片URL数组
   * @returns 处理后的封面图片URL数组
   */
  static async processFolderCovers(covers: string[]): Promise<{
    single?: string;
    dual?: [string, string];
    triple?: [string, string, string];
  }> {
    if (covers.length === 0) return {};

    if (covers.length === 1) {
      return {
        single: await this.processOSSImage(covers[0], 400, 180, { mode: 'fill' }),
      };
    }

    if (covers.length === 2) {
      const [first, second] = await Promise.all([
        this.processOSSImage(covers[0], 200, 180, { mode: 'fill' }),
        this.processOSSImage(covers[1], 200, 180, { mode: 'fill' }),
      ]);
      return {
        dual: [first, second],
      };
    }

    // 3个及以上
    const [first, second, third] = await Promise.all([
      this.processOSSImage(covers[0], 200, 180, { mode: 'fill' }),
      this.processOSSImage(covers[1], 200, 90, { mode: 'fill' }),
      this.processOSSImage(covers[2], 200, 90, { mode: 'fill' }),
    ]);
    return {
      triple: [first, second, third],
    };
  }

  /**
   * 为资产卡片处理缩略图
   * @param url 原始图片URL
   * @param size 缩略图尺寸类型
   * @returns 处理后的缩略图URL
   */
  static async processAssetThumbnail(
    url: string,
    size: 'small' | 'medium' | 'large' = 'medium'
  ): Promise<string> {
    const sizeMap = {
      small: { width: 132, height: 132 },
      medium: { width: 200, height: 200 },
      large: { width: 400, height: 400 },
    };

    const { width, height } = sizeMap[size];
    return this.processOSSImage(url, width, height, {
      mode: 'fill',
      quality: 50,
      format: 'webp',
    });
  }

  /**
   * 同步版本的缩略图处理（不使用WebP自动检测）
   */
  static processAssetThumbnailSync(
    url: string,
    size: 'small' | 'medium' | 'large' = 'medium'
  ): string {
    if (!url) return url;

    const sizeMap = {
      small: { width: 132, height: 132 },
      medium: { width: 200, height: 200 },
      large: { width: 400, height: 400 },
    };

    // 检查是否为阿里云OSS图片
    if (!this.isOSSImage(url)) {
      return url;
    }

    const { width, height } = sizeMap[size];
    const params = [
      `resize,m_fill,w_${width},h_${height}`,
      'auto-orient,1',
      'quality,q_50',
      'format,webp', // 直接使用webp，现代浏览器都支持
    ].join('/');

    return `${url}?x-oss-process=image/${params}`;
  }

  /**
   * 同步版本的OSS图片处理（不使用WebP自动检测）
   */
  static processOSSImageSync(
    url: string,
    width: number,
    height: number,
    options: {
      mode?: 'fill' | 'fit' | 'pad';
      quality?: number;
      format?: 'webp' | 'jpg' | 'png';
      autoOrient?: boolean;
    } = {}
  ): string {
    if (!url) return url;

    const { mode = 'fill', quality = 50, format = 'webp', autoOrient = true } = options;

    // 检查是否为阿里云OSS图片
    if (!this.isOSSImage(url)) {
      return url;
    }

    const params = [
      `resize,m_${mode},w_${width},h_${height}`,
      ...(autoOrient ? ['auto-orient,1'] : []),
      `quality,q_${quality}`,
      `format,${format}`,
    ].join('/');

    return `${url}?x-oss-process=image/${params}`;
  }

  /**
   * 检查是否为支持图片处理的CDN图片
   * @param url 图片URL
   * @returns 是否为支持的CDN图片
   */
  private static isOSSImage(url: string): boolean {
    if (!url) return false;

    // 阿里云OSS
    if (url.includes('aliyuncs.com') || url.includes('oss-')) {
      return true;
    }

    // 万界数据CDN (支持阿里云OSS处理参数)
    if (url.includes('wanjiedata.com')) {
      return true;
    }

    // CDN (支持阿里云OSS处理参数)
    if (url.includes('cdn')) {
      return true;
    }

    return false;
  }

  /**
   * 获取图片的原始尺寸信息（通过OSS info接口）
   * @param url 图片URL
   * @returns Promise<{width: number, height: number} | null>
   */
  static async getImageInfo(url: string): Promise<{ width: number; height: number } | null> {
    if (!url || !this.isOSSImage(url)) {
      return null;
    }

    try {
      // 去掉可能已有的 x-oss-process（如缩略图参数），只对原图查询元信息；
      // 兼容带签名/查询串的 URL，用正确的分隔符拼接。
      const base = url.split(/[?&]x-oss-process=/)[0];
      const separator = base.includes('?') ? '&' : '?';
      const infoUrl = `${base}${separator}x-oss-process=image/info`;
      const response = await fetch(infoUrl);
      if (!response.ok) return null;
      const info = await response.json();

      const width = Number(info?.ImageWidth?.value) || 0;
      const height = Number(info?.ImageHeight?.value) || 0;
      if (!width || !height) return null;
      return { width, height };
    } catch (error) {
      console.warn('获取图片信息失败:', error);
      return null;
    }
  }

  /**
   * 生成响应式图片URL集合
   * @param url 原始图片URL
   * @param aspectRatio 宽高比 (width/height)
   * @returns 不同尺寸的图片URL对象
   */
  static async generateResponsiveImages(
    url: string,
    aspectRatio: number = 1
  ): Promise<{
    xs: string; // 移动端小屏
    sm: string; // 移动端
    md: string; // 平板
    lg: string; // 桌面端
    xl: string; // 大屏
  }> {
    const sizes = {
      xs: { width: 150, height: Math.round(150 / aspectRatio) },
      sm: { width: 200, height: Math.round(200 / aspectRatio) },
      md: { width: 300, height: Math.round(300 / aspectRatio) },
      lg: { width: 400, height: Math.round(400 / aspectRatio) },
      xl: { width: 600, height: Math.round(600 / aspectRatio) },
    };

    const results = await Promise.all(
      Object.entries(sizes).map(async ([key, { width, height }]) => [
        key,
        await this.processOSSImage(url, width, height, {
          mode: 'fit',
          quality: 50,
          format: 'webp',
        }),
      ])
    );

    return results.reduce((acc, [key, url]) => {
      acc[key as keyof typeof acc] = url;
      return acc;
    }, {} as any);
  }

  /**
   * 预加载图片
   */
  static preloadImage(
    url: string,
    options: {
      width?: number;
      height?: number;
      mode?: 'fill' | 'fit' | 'pad';
      quality?: number;
      format?: 'webp' | 'jpg' | 'png' | 'auto';
    } = {}
  ): Promise<void> {
    return new Promise(async (resolve, reject) => {
      try {
        const processedUrl =
          options.width && options.height
            ? await this.processOSSImage(url, options.width, options.height, options)
            : url;

        const img = new Image();
        img.onload = () => resolve();
        img.onerror = () => reject(new Error(`Failed to preload image: ${processedUrl}`));
        img.src = processedUrl;
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * 批量预加载图片
   */
  static async preloadImages(
    urls: string[],
    options: {
      width?: number;
      height?: number;
      mode?: 'fill' | 'fit' | 'pad';
      quality?: number;
      format?: 'webp' | 'jpg' | 'png' | 'auto';
    } = {},
    concurrency: number = 3
  ): Promise<void> {
    const chunks: string[][] = [];

    // 分批处理
    for (let i = 0; i < urls.length; i += concurrency) {
      chunks.push(urls.slice(i, i + concurrency));
    }

    for (const chunk of chunks) {
      await Promise.allSettled(chunk.map(url => this.preloadImage(url, options)));
    }
  }

  /**
   * 清除缓存
   */
  static clearCache(): void {
    this.urlCache.clear();
  }

  /**
   * 获取缓存统计信息
   */
  static getCacheStats(): {
    size: number;
    count: number;
    maxSize: number;
    maxAge: number;
  } {
    let totalSize = 0;
    for (const cached of this.urlCache.values()) {
      totalSize += cached.size;
    }

    return {
      size: Math.round((totalSize / 1024 / 1024) * 100) / 100, // MB
      count: this.urlCache.size,
      maxSize: this.cacheConfig.maxSize,
      maxAge: this.cacheConfig.maxAge,
    };
  }

  /**
   * 处理图片 URL，添加阿里云 OSS 压缩参数（简化版）
   * 专门针对瀑布流展示场景优化，限制最大宽度为 600px，减少带宽消耗
   * @param url 原始图片URL
   * @returns 处理后的图片URL
   */
  static getOptimizedImageUrlSimple(url: string | undefined): string {
    // 空值处理：返回空字符串避免 undefined 导致的 DOM 警告
    if (!url) return '';

    // 检查是否已经包含 x-oss-process 参数
    if (url.includes('x-oss-process=image')) {
      return url;
    }

    // 为瀑布流图片添加尺寸限制和质量压缩
    // 最大宽度 600px 适配最大列宽场景（6列布局时单列约 320px，考虑高 DPI 屏幕）
    const params = [
      'resize,m_lfit,w_600', // lfit 模式：等比缩放，不超过 600px 宽度
      'quality,q_50', // 质量 50，在视觉效果和文件大小间取得平衡
      'format,webp', // WebP 格式，现代浏览器都支持，压缩率更高
    ].join('/');

    // 修复 URL 拼接问题：如果已有查询参数，使用 & 连接
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}x-oss-process=image/${params}`;
  }

  /**
   * H5 瀑布流图片优化（宽度 400px，适配 2 列布局 + 高 DPI 屏幕）
   * H5 单列实际宽度约 170px，2x DPI 需要 ~340px，400px 留有余量
   */
  static getOptimizedImageUrlForH5(url: string | undefined): string {
    if (!url) return '';
    if (url.includes('x-oss-process=image')) return url;

    const params = [
      'resize,m_lfit,w_400',
      'quality,q_60',
      'format,webp',
    ].join('/');

    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}x-oss-process=image/${params}`;
  }

  /**
   * 头像 OSS 优化（80x80 正方形裁剪）
   */
  static getOptimizedAvatarUrl(url: string | undefined): string {
    if (!url) return '';
    if (url.includes('x-oss-process=image')) return url;

    const params = [
      'resize,m_fill,w_80,h_80',
      'quality,q_60',
      'format,webp',
    ].join('/');

    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}x-oss-process=image/${params}`;
  }

  /**
   * 画布节点主预览图（等比缩放不超 800px，适配高 DPI 屏幕 + 节点缩放）
   * 仅用于 img/background-image 展示，下载/API payload/全屏预览请用原图
   */
  static getCanvasPreviewUrl(url: string | undefined): string {
    if (!url) return '';
    if (url.includes('x-oss-process=image')) return url;
    if (!this.isOSSImage(url)) return url;

    const params = [
      'resize,m_lfit,w_800',
      'quality,q_50',
      'format,webp',
    ].join('/');

    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}x-oss-process=image/${params}`;
  }

  /**
   * 画布节点小缩略图（叠层背景装饰图、面板内上游图 icon 等）
   * 仅用于展示，下载/API payload/全屏预览请用原图
   */
  static getCanvasThumbnailUrl(url: string | undefined): string {
    if (!url) return '';
    if (url.includes('x-oss-process=image')) return url;
    if (!this.isOSSImage(url)) return url;

    const params = [
      'resize,m_lfit,w_200',
      'quality,q_50',
      'format,webp',
    ].join('/');

    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}x-oss-process=image/${params}`;
  }
}

// 导出便捷函数
export const processOSSImage = ImageProcessor.processOSSImage.bind(ImageProcessor);
export const processOSSImageSync = ImageProcessor.processOSSImageSync.bind(ImageProcessor);
export const processFolderCovers = ImageProcessor.processFolderCovers.bind(ImageProcessor);
export const processAssetThumbnail = ImageProcessor.processAssetThumbnail.bind(ImageProcessor);
export const processAssetThumbnailSync =
  ImageProcessor.processAssetThumbnailSync.bind(ImageProcessor);
export const generateResponsiveImages =
  ImageProcessor.generateResponsiveImages.bind(ImageProcessor);
export const getOptimizedImageUrlSimple =
  ImageProcessor.getOptimizedImageUrlSimple.bind(ImageProcessor);
export const getOptimizedImageUrlForH5 =
  ImageProcessor.getOptimizedImageUrlForH5.bind(ImageProcessor);
export const getOptimizedAvatarUrl =
  ImageProcessor.getOptimizedAvatarUrl.bind(ImageProcessor);
export const getCanvasPreviewUrl =
  ImageProcessor.getCanvasPreviewUrl.bind(ImageProcessor);
export const getCanvasThumbnailUrl =
  ImageProcessor.getCanvasThumbnailUrl.bind(ImageProcessor);
// 查询 OSS 图片原始尺寸（仅几百字节 JSON，不下载像素）
export const getOSSImageInfo =
  ImageProcessor.getImageInfo.bind(ImageProcessor);
